import React, { useRef, useState } from 'react';
import { Typography, TextField, Button, Link, Alert, Box, Divider, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import { useNavigate } from "react-router-dom";

import { initiateRegisterUser, verifyRegisterUser } from '../../services/userService';
import { useUser } from '../../UserContext';
import { useAuth } from '../../AuthContext';
import { Utils } from '../../utils/utils';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';

import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { getMonthsAndDays, getYearList } from '../../utils/dateFunctions';
import { APP_NAME } from '../../constants';

import { jwtDecode } from "jwt-decode";
import theme from '../../theme';

const userTypeOptions = [
  { value: 'customer', label: 'Customer' },
  { value: 'chef', label: 'Chef' },
];

const RegisterUser = () => {
  const { setUser } = useUser();
  const { setAuth } = useAuth();
  const defaultUser = { email: '', password: '', type: 'customer', dob: { year: '', month: '', day: ''} };
  const [newUserData, setNewUserData] = useState(defaultUser);
  const [error, setError] = useState(null);
  const [userAccountExists, setUserAccountExists] = useState(false);
  const [dayList, setDayList] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: '',
    dob: ''
  });
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationDigits, setVerificationDigits] = useState(Array(6).fill(''));
  const [verificationError, setVerificationError] = useState('');
  const [registrationId, setRegistrationId] = useState('');
  const [pendingUserData, setPendingUserData] = useState(null);
  const verificationInputRefs = useRef([]);

  const navigate = useNavigate();

  const months = getMonthsAndDays();
  const yearList = getYearList();

  const handleDob = (e) => {
    const { name, value } = e.target;

    if (name === 'year') {
      setNewUserData({...newUserData, dob: {...newUserData.dob, year: value}})
    } else if (name === 'month') {
      setNewUserData({...newUserData, dob: {...newUserData.dob, month: value}})
    } else {
      setNewUserData({...newUserData, dob: {...newUserData.dob, day: value}})
    }

    if (name === 'year' || name === 'month') {
      const days = getMonthsAndDays(newUserData.dob.year).find(m => m.index === value)?.days;
      let DaysArray = [];
      for (let index = 1; index <= days; index++) {
        const day = index;
        DaysArray.push(day);
      }

      setDayList(DaysArray);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUserData((prevUser) => ({
      ...prevUser,
      [name]: value,
    }));

    if (name === 'email') {
      if (!value.trim()) {
        setValidationErrors((prev) => ({
          ...prev,
          email: ''
        }));
      } else if (!Utils.validateEmailFormat(value)) {
        setValidationErrors((prev) => ({
          ...prev,
          email: 'Please enter a valid email address'
        }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          email: ''
        }));
      }
      return;
    }
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  function handleUserTypeChange(valueSelected) {
    const type = valueSelected.target.value;
    setNewUserData({...newUserData, type: type, dob: { year: '', month: '', day: '' }});
    setDayList([]);
    
    // Clear DOB validation error when changing type
    if (validationErrors.dob) {
      setValidationErrors((prev) => ({
        ...prev,
        dob: ''
      }));
    }
  }

  const validateForm = () => {
    const errors = {
      email: '',
      password: '',
      dob: ''
    };
    
    let isValid = true;
    
    // Validate email
    if (!newUserData.email || newUserData.email.trim() === '') {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!Utils.validateEmailFormat(newUserData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }
    
    // Validate password
    if (!newUserData.password || newUserData.password.trim() === '') {
      errors.password = 'Password is required';
      isValid = false;
    } else if (newUserData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
      isValid = false;
    }
    
    // Validate DOB for chef
    if (newUserData.type === 'chef') {
      if (!newUserData.dob.year || !newUserData.dob.month || !newUserData.dob.day) {
        errors.dob = 'Date of birth is required for chefs';
        isValid = false;
      }
    }
    
    setValidationErrors(errors);
    return isValid;
  };

  const buildRegisterPayload = (userData) => {
    const payload = {
      email: userData.email,
      password: userData.password,
      type: userData.type,
    };

    if (userData.type === 'chef') {
      payload.dob = {
        year: Number(userData.dob.year),
        month: Number(userData.dob.month),
        day: Number(userData.dob.day),
      };
    }

    return payload;
  };

  const handleRegistrationSuccess = (registeredUserResponse) => {
    const decodedToken = jwtDecode(registeredUserResponse.token);
    const userRegistered = decodedToken?.userDTO;

    if (userRegistered.id < 0) {
      setUserAccountExists(true);
      return;
    }

    setAuth(registeredUserResponse.token);
    setUser(userRegistered);
    if (Utils.isCustomer(userRegistered)) {
      navigate("/list");
    } else if (Utils.isChef(userRegistered)) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  }

  const closeVerificationDialog = () => {
    setVerificationDialogOpen(false);
    setVerificationDigits(Array(6).fill(''));
    setVerificationError('');
  }

  const handleVerificationDigitChange = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    const nextDigits = [...verificationDigits];
    nextDigits[index] = digit;
    setVerificationDigits(nextDigits);

    if (verificationError) {
      setVerificationError('');
    }

    if (digit && index < 5) {
      verificationInputRefs.current[index + 1]?.focus();
    }
  };

  const handleVerificationDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !verificationDigits[index] && index > 0) {
      verificationInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerificationCodePaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) {
      return;
    }

    event.preventDefault();
    const nextDigits = Array(6).fill('');
    pasted.split('').forEach((digit, index) => {
      nextDigits[index] = digit;
    });
    setVerificationDigits(nextDigits);

    const focusIndex = Math.min(pasted.length, 5);
    verificationInputRefs.current[focusIndex]?.focus();

    if (verificationError) {
      setVerificationError('');
    }
  };

  async function register()  {

    // Reset previous errors
    setError('');
    setUserAccountExists(false);
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    const registerPayload = buildRegisterPayload(newUserData);
    
    try {
        const initiateResponse = await initiateRegisterUser(registerPayload);
        if (initiateResponse?.registrationId) {
          setPendingUserData(registerPayload);
          setRegistrationId(initiateResponse.registrationId);
          setVerificationDigits(Array(6).fill(''));
          setVerificationError('');
          setVerificationDialogOpen(true);
        }
    } catch (error) {
        if (error.code === 409) {
          setUserAccountExists(true);
        } else {
          setError(error.message);
        }
    }
  }

  async function verifyRegistrationCode() {
    setError('');
    setVerificationError('');
    const verificationCode = verificationDigits.join('');

    if (!/^\d{6}$/.test(verificationCode)) {
      setVerificationError('Please enter a valid 6-digit code');
      return;
    }

    try {
      const verifyRequest = {
        ...pendingUserData,
        registrationId,
        emailVerificationCode: verificationCode,
      };

      const verifyResponse = await verifyRegisterUser(verifyRequest);
      if (verifyResponse?.token) {
        closeVerificationDialog();
        handleRegistrationSuccess(verifyResponse);
      }
    } catch (error) {
      setVerificationError(error.message || 'Unable to verify code. Please try again.');
    }
  }

  return (

        <div style={{ display: 'flex', maxWidth: 520, flexDirection: 'column'}}>

            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <Typography variant="h5">
                {`Register on ${APP_NAME}`}
                </Typography>
            </div>

            { error && (
              <Alert severity="error" style={{ marginTop: 16 }}>
                Sorry, something went wrong.
              </Alert>
            ) }
            
            { userAccountExists && (
              <Alert severity="error" style={{ marginTop: 16 }} data-testid="user-account-exists">
                An account with the same email already exists
              </Alert>
            ) }
            
            <form>

                <FormControl style={{ display: 'flex', marginTop: 16 }}>
                      <FormLabel id="user-type-label">I want to be a:</FormLabel>
                      <RadioGroup
                        aria-labelledby="user-type-label"
                        name="row-radio-buttons-group"
                        row
                        value={newUserData.type}
                        onChange={handleUserTypeChange}
                        style={{ display: 'flex', gap: 12, marginTop: 12 }}
                      >
                        {userTypeOptions.map((option) => {
                          const isSelected = newUserData.type === option.value;

                          return (
                            <Box
                              key={option.value}
                              component="div"
                              data-testid={`${option.value}-option-card`}
                              onClick={() => handleUserTypeChange({ target: { value: option.value } })}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                cursor: 'pointer',
                                borderRadius: 12,
                                border: `2px solid ${isSelected ? theme.palette.primary.main : '#d0d7de'}`,
                                backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
                                transition: 'all 0.2s ease',
                                boxShadow: isSelected ? `0 0 0 1px ${theme.palette.primary.main}1A` : 'none',
                              }}
                            >
                              <FormControlLabel
                                value={option.value}
                                control={<Radio />}
                                label={<span style={{ fontWeight: isSelected ? 600 : 500 }}>{option.label}</span>}
                                data-testid={`${option.value}-radio`}
                                style={{
                                  margin: 0,
                                  width: '100%',
                                  minHeight: 72,
                                  paddingRight: 16,
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              />
                            </Box>
                          );
                        })}
                      </RadioGroup>
                </FormControl>

                <TextField
                    label="Email"
                    name="email"
                    value={newUserData.email}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                    error={!!validationErrors.email}
                    helperText={validationErrors.email}
                    data-testid="email-input"
                />

                <TextField
                    label="Password"
                    name="password"
                    value={newUserData.password}
                    type='password'
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                    error={!!validationErrors.password}
                    helperText={validationErrors.password}
                    data-testid="password-input"
                />

                

                <div style={{ display: newUserData.type === 'chef' ? 'flex' : 'none', flexDirection: 'column', marginTop: 16 }}>
                  <label style={{ color: validationErrors.dob ? '#d32f2f' : '#666666'}}>
                    Date Of Birth {newUserData.type === 'chef' && '*'}
                  </label>
                  {validationErrors.dob && (
                    <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                      {validationErrors.dob}
                    </Typography>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'row', gap: 8, justifyContent: 'flex-start', marginTop: 16 }}>

                    <FormControl style={{ minWidth: 100 }} error={!!validationErrors.dob}>
                      <InputLabel id="year-label">Year</InputLabel>
                      <Select
                          id="select-dob-year"
                          labelId="year-label"
                          value={newUserData.dob.year}
                          label="Year"
                          name='year'
                          onChange={handleDob}
                          data-testid="dob-year-select"
                        >
                        {yearList.map((year) => <MenuItem value={year}>{year}</MenuItem> )}
                      </Select>

                    </FormControl>

                    <FormControl style={{ minWidth: 100 }} error={!!validationErrors.dob}>
                      <InputLabel id="month-label">Month</InputLabel>
                      <Select
                          id="select-dob-month"
                          labelId="month-label"
                          value={newUserData.dob.month}
                          label="Month"
                          name='month'
                          onChange={handleDob}
                          data-testid="dob-month-select"
                        >
                        {months.map((m) => <MenuItem value={m.index}>{m.month}</MenuItem> )}
                      </Select>
                    </FormControl>

                    <FormControl style={{ minWidth: 80 }} error={!!validationErrors.dob}>
                      <InputLabel id="month-label">Day</InputLabel>
                      <Select
                          id="select-dob-day"
                          labelId="day-label"
                          value={newUserData.dob.day}
                          label="Day"
                          name='day'
                          disabled={newUserData.dob.year && newUserData.dob.month ? false : true}
                          onChange={handleDob}
                          data-testid="dob-day-select"
                        >
                        {dayList.map((day) => <MenuItem value={day}>{day}</MenuItem> )}
                      </Select>

                    </FormControl>

                  </div>
                </div>

                <Divider style={{ margin: '24px 0' }} />

                <div style={{ display: 'flex', gap:16, justifyContent: 'right', marginTop: 16}}>
                    <Link href='login' style={{ cursor: 'pointer', textDecoration: 'none'}} >Back to Welcome Page</Link>
                    <Button style={{ textTransform: 'none'}} onClick={register} variant="contained" color="primary" data-testid="register-button">
                        Register
                    </Button>
                </div>
            </form>

            <Dialog open={verificationDialogOpen} onClose={closeVerificationDialog} fullWidth maxWidth="xs" data-testid="verification-code-dialog">
              <DialogTitle>Email Verification</DialogTitle>
              <DialogContent>
                <Typography variant="body2" style={{ marginBottom: 12 }}>
                  Enter the 6-digit code sent to {newUserData.email}
                </Typography>
                <Box
                  data-testid="verification-code-input"
                  style={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    marginTop: 8,
                    padding: 12,
                  }}
                >
                  <Box style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                    {verificationDigits.map((digit, index) => (
                      <TextField
                        key={`verification-digit-${index}`}
                        value={digit}
                        onChange={(event) => handleVerificationDigitChange(index, event.target.value)}
                        onKeyDown={(event) => handleVerificationDigitKeyDown(index, event)}
                        onPaste={handleVerificationCodePaste}
                        inputRef={(element) => {
                          verificationInputRefs.current[index] = element;
                        }}
                        autoFocus={index === 0}
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          maxLength: 1,
                          style: { textAlign: 'center' },
                        }}
                        data-testid={`verification-code-digit-${index}`}
                        style={{ width: 44 }}
                      />
                    ))}
                  </Box>
                </Box>
                {verificationError && (
                  <Typography variant="caption" color="error" style={{ marginTop: 8, display: 'block' }}>
                    {verificationError}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeVerificationDialog} data-testid="verification-cancel-button">Cancel</Button>
                <Button onClick={verifyRegistrationCode} variant="contained" data-testid="verification-submit-button" disabled={verificationDigits.join('').length !== 6}>
                  Verify
                </Button>
              </DialogActions>
            </Dialog>
        </div>

  );
};

export default RegisterUser;
